/**
 * Schema.org / JSON-LD builders.
 *
 * Returns plain objects that are serialized into <script type="application/ld+json">
 * tags via useHead. During SSG these land in the static HTML, which is what AI
 * crawlers and search engines read as the most reliable source for entities and facts.
 *
 * Keep the canonical "Quatico ist …" facts here as the single source of truth so the
 * entity signal stays consistent across every page.
 */

import type {
    AnswerLeaf,
    BlogPostingLeaf,
    BreadcrumbListLeaf,
    FAQPageLeaf,
    IdReference,
    ListItem,
    OrganizationLeaf,
    PersonLeaf,
    QuestionLeaf,
    ServiceLeaf,
    SoftwareApplicationLeaf,
    WebSiteLeaf,
    WithContext,
} from "schema-dts";

const BASE_URL = "https://www.quatico.com";

const ORGANIZATION_ID = `${BASE_URL}/#organization`;
const WEBSITE_ID = `${BASE_URL}/#website`;

/** schema-dts 2.0 inherits @id via Partial<IdReference>, which TS won't accept on object literals under strict mode. */
type WithEntityId<T> = T & Pick<IdReference, "@id">;

/**
 * The Organization entity — the anchor every other schema references via @id.
 * Sourced from content/de/components/footer.mdx and content/de/pages/about-us.mdx.
 */
export const buildOrganizationSchema = (language: "de" | "en" = "de"): WithContext<WithEntityId<OrganizationLeaf>> => {
    // Describes the COMPANY, not the homepage: what we build, how, and the proof —
    // in the first two sentences, so an agent reading only the entity gets a usable
    // answer to "what does Quatico do?" (Konzept § 11 Baustein 2).
    //
    // NOT kept in sync with the homepage SEO block, and the comment said otherwise
    // until 15.9.2026. That day the homepage moved to "Ihre Anwendung, in 30 Tagen
    // startklar" while this text kept the engineering-house wording — correct, but
    // the claim of synchrony was false and would have sent the next reader looking
    // for a mismatch to fix.
    //
    // The two are covered separately: this description by `about-us.mdx` (the
    // engineering-house sentence, visible in its subtitle) and by `referenzen.mdx`
    // (the 2.5 million). Both are where the assistant's `quelle` points.
    const description =
        language === "en"
            ? "Quatico is a Swiss engineering house founded in 2003. Quatico turns business processes into " +
              "software — advised, built, or run as a service — for SMEs and enterprise departments, building " +
              "custom software and AI agents with agentic development. Software by Quatico is used by 2.5 million people."
            : "Quatico ist ein Schweizer Engineering-Haus, gegründet 2003. Quatico verwandelt Geschäftsprozesse " +
              "in Software — beraten, gebaut oder als Service betrieben — für KMU und Enterprise-Abteilungen und " +
              "entwickelt Individualsoftware und KI-Agenten mit Agentic Development. Software von Quatico wird von " +
              "2.5 Millionen Menschen genutzt.";

    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: "Quatico Solutions AG",
        legalName: "Quatico Solutions AG",
        url: BASE_URL,
        logo: `${BASE_URL}/images/quatico-logo.png`,
        description,
        foundingDate: "2003",
        email: "info@quatico.com",
        telephone: "+41 43 243 88 00",
        address: {
            "@type": "PostalAddress",
            streetAddress: "Förrlibuckstrasse 220",
            postalCode: "8005",
            addressLocality: "Zürich",
            addressCountry: "CH",
        },
        sameAs: ["https://www.linkedin.com/company/quatico", "https://x.com/quatico", "https://github.com/quatico"],
    };
};

/**
 * The WebSite entity — ties the domain to the Organization as its publisher.
 */
export const buildWebSiteSchema = (language: "de" | "en" = "de"): WithContext<WithEntityId<WebSiteLeaf>> => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: BASE_URL,
    name: "Quatico",
    inLanguage: language === "en" ? "en" : "de-CH",
    publisher: { "@id": ORGANIZATION_ID },
});

type BlogPostingInput = {
    title: string;
    description: string;
    canonicalPath: string;
    publishedAt: string;
    updatedAt?: string;
    image?: string | null;
    author: { name: string; role?: string; image?: string };
    language: "de" | "en";
    keywords?: string[];
    section?: string;
};

/**
 * BlogPosting entity for blog articles. The author is modeled as a Person so AI
 * engines can attribute expertise (an E-E-A-T signal).
 */
export const buildBlogPostingSchema = (post: BlogPostingInput): WithContext<BlogPostingLeaf> => {
    const url = `${BASE_URL}${post.canonicalPath}`;
    const author: PersonLeaf = {
        "@type": "Person",
        name: post.author.name,
        // Anchor the author to the publisher so AI engines attribute the article's
        // expertise to a Quatico employee — an E-E-A-T signal that does not depend
        // on the displayed name (we intentionally show only the first name).
        worksFor: { "@id": ORGANIZATION_ID },
        ...(post.author.role ? { jobTitle: post.author.role } : {}),
        ...(post.author.image ? { image: `${BASE_URL}${post.author.image}` } : {}),
    };

    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        headline: post.title,
        description: post.description,
        ...(post.image ? { image: `${BASE_URL}${post.image}` } : {}),
        datePublished: post.publishedAt,
        dateModified: post.updatedAt || post.publishedAt,
        inLanguage: post.language === "en" ? "en" : "de-CH",
        author,
        publisher: { "@id": ORGANIZATION_ID },
        ...(post.section ? { articleSection: post.section } : {}),
        ...(post.keywords && post.keywords.length > 0 ? { keywords: post.keywords.join(", ") } : {}),
    };
};

type SoftwareApplicationInput = {
    name: string;
    description: string;
    url: string;
    price?: string;
    priceCurrency?: string;
    /**
     * Billing interval the price recurs on, as a UN/CEFACT code: "MON" (per month)
     * or "ANN" (per year). When set, the price is modeled as a recurring rate via
     * UnitPriceSpecification instead of a one-off amount.
     */
    billingUnit?: "MON" | "ANN";
};

/**
 * SoftwareApplication entity for the configurable product pages (Vertriebsportal
 * editions). Carries an Offer when a price is known, so AI engines and Rich
 * Results can surface the product with its pricing.
 *
 * Subscription prices (billingUnit set) are expressed as a UnitPriceSpecification
 * with a reference quantity, so "99" reads as "CHF 99 per month" rather than a
 * misleading one-time charge.
 */
export const buildSoftwareApplicationSchema = (
    product: SoftwareApplicationInput
): WithContext<SoftwareApplicationLeaf> => {
    const currency = product.priceCurrency || "CHF";

    return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: product.name,
        description: product.description,
        url: `${BASE_URL}${product.url}`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        ...(product.price
            ? {
                  offers: {
                      "@type": "Offer",
                      priceCurrency: currency,
                      availability: "https://schema.org/InStock",
                      url: `${BASE_URL}${product.url}`,
                      priceSpecification: {
                          "@type": "UnitPriceSpecification",
                          price: product.price,
                          priceCurrency: currency,
                          ...(product.billingUnit
                              ? {
                                    referenceQuantity: {
                                        "@type": "QuantitativeValue",
                                        value: "1",
                                        unitCode: product.billingUnit,
                                    },
                                }
                              : {}),
                      },
                  },
              }
            : {}),
    };
};

/**
 * Service entity for offer detail pages (Relaunch Phase 5, Eckpfeiler 1). Tells AI
 * engines that Quatico provides this service and who provides it. Deliberately carries
 * NO `offers`/`Offer` node: the Konzept-§ 5 offer pages sell a service, not a priced
 * product, and 00-inventar.md:282 asks only for `Service` on offer pages — `Offer`,
 * DefinedTerm and the glossary belong to a later SEO stage (ai-crawler-aeo-foundation),
 * not here. Keeping this builder Offer-free is what stops that scope from leaking in.
 */
export const buildServiceSchema = (service: {
    name: string;
    description: string;
    url: string;
    serviceType?: string;
}): WithContext<ServiceLeaf> => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.description,
    url: `${BASE_URL}${service.url}`,
    provider: { "@id": ORGANIZATION_ID },
    ...(service.serviceType ? { serviceType: service.serviceType } : {}),
});

/**
 * BreadcrumbList entity for deep pages. Gives AI engines and search the page's
 * place in the site hierarchy; paths are resolved to absolute canonical URLs.
 */
export const buildBreadcrumbSchema = (
    items: Array<{ name: string; path: string }>
): WithContext<BreadcrumbListLeaf> => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(
        (item, index): ListItem => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: `${BASE_URL}${item.path}`,
        })
    ),
});

/**
 * FAQPage entity for pages that answer recurring questions. Google restricts FAQ
 * rich results to authoritative domains, but the markup remains a strong AEO
 * signal: AI engines read these question/answer pairs as authoritative facts.
 */
export const buildFaqSchema = (items: Array<{ question: string; answer: string }>): WithContext<FAQPageLeaf> => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(
        (item): QuestionLeaf => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer } satisfies AnswerLeaf,
        })
    ),
});
